import { WSQueryCache } from "@ws-react-query";
import {
  queriesPayeeEngagements,
  queriesPayeeRows,
  queriesPayees,
  queriesEngagements
} from "@query";

import { payeeEngagementsApi } from "@api";

import { PayeeEngagementType } from "@wingspanhq/wingspan-api-sdk";
import {
  bulkActionFactory,
  BulkActionProgress,
  useForegroundBulkAction
} from "../../BulkActions";
import { useXWingspanPayerId } from "../routes/RoutePayeeDetails/useXWingspanPayerId";

export interface AssignEngagementBulkItem {
  payeeId: string;
  engagementId: string;
  name?: string;
}

export const useAssignEngagementsBulkAction = (): ((
  items: AssignEngagementBulkItem[]
) => Promise<boolean>) => {
  const runBulkAction = useForegroundBulkAction();
  const xWingspanPayerId = useXWingspanPayerId();

  return async items => {
    let didRun = false;
    await runBulkAction({
      actionName: "Add assignments",
      itemCount: items.length,
      skipConfirm: true,
      prepare: async () => items,
      process: async (data, onProgress): Promise<BulkActionProgress> => {
        didRun = true;
        const bulk = bulkActionFactory<AssignEngagementBulkItem>({
          action: async item => {
            // v3 `createPayeeEngagement` requires `engagementType`, which v1 never
            // sent — read it off the engagement template. `fetchQueryData` is
            // cache-first, so repeated engagementIds across the batch hit the cache.
            const engagementTemplate =
              await queriesEngagements.getEngagement.fetchQueryData({
                engagementId: item.engagementId,
                xWingspanAccount: xWingspanPayerId
              });

            return payeeEngagementsApi.createPayeeEngagement({
              payeeId: item.payeeId,
              payeeEngagementCreate: {
                engagementId: item.engagementId,
                engagementType:
                  engagementTemplate.engagementType ??
                  PayeeEngagementType.CONTRACTOR
              },
              xWingspanAccount: xWingspanPayerId
            });
          },
          getSuccessDescription: item =>
            item.name ? `Assigned "${item.name}"` : "",
          getProgressMessage: (item, progress) =>
            `Assigning engagement ${Math.min(
              progress.processed + 1,
              progress.total
            )} of ${progress.total}...`,
          getFailedDescription: item =>
            item.name ? `Assign "${item.name}"` : "Assign engagement",
          onProgress
        });

        const { failedItems, successItems } = await bulk.run(data);

        WSQueryCache.invalidateQueries(
          queriesPayeeEngagements.getPayeeEngagement.queryKeyBase
        );
        WSQueryCache.invalidateQueries(
          queriesPayeeEngagements.listPayeeEngagementsByPayer.queryKeyBase
        );
        WSQueryCache.invalidateQueries(
          queriesPayeeEngagements.listPayeeEngagements.queryKeyBase
        );
        // The payee entity carries `activeEngagementTypes`, so a new assignment
        // changes it. Was v1 `QUERY_PAYEE` — an orphan constant no hook has
        // populated since the payee read moved to the registry.
        WSQueryCache.invalidateQueries(queriesPayees.getPayee.queryKeyBase);
        // Was v1 `QUERY_PAYEE_ROWS`; the v3 search rows below replace it.
        WSQueryCache.invalidateQueries(
          queriesPayeeRows.searchPayeeRows.queryKeyBase
        );
        // v1 `QUERY_PAYEE_ROWS_SUMMARY` was invalidated here too. Dropped, not
        // ported: `PayeeRowsApi` exposes only `searchPayeeRows` — v3 has no
        // row-summary endpoint at all (SDK 3.52.6), and the v1 hook behind that
        // key throws `NotMigrated`, so the invalidation was a no-op either way.
        // See V3_MIGRATION_TRACKER.md · /v3/search/payee-rows.

        return {
          failed: failedItems.length,
          failedDescriptions: failedItems.map(item =>
            item.name ? `Assign "${item.name}"` : "Assign engagement"
          ),
          processed: successItems.length + failedItems.length,
          successful: successItems.length,
          successDescriptions: successItems
            .filter(item => item.name)
            .map(item => `Assigned "${item.name}"`),
          total: data.length
        };
      }
    });
    return didRun;
  };
};
